import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  ChevronDown, 
  User, 
  LogOut,
  Menu,
  X,
  LayoutDashboard,
  Crown,
  Bell,
  Star,
  Shield,
  TrendingUp,
  Sun,
  Moon,
  Home,
  Building2,
  LineChart,
  Award,
  Calendar,
  Newspaper,
  Activity,
  BookOpen,
  Layers,
  Users,
  Search,
  ChevronRight,
  Zap,
  Globe,
  Eye,
  EyeOff
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import api from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { languageCodes, defaultLanguage, languages } from '../i18n';

// Navigation items with translation keys and icons
const getNavItems = (t) => [
  { name: t('nav.home', 'Portal'), path: '/', icon: Home },
  { name: t('nav.brokers', 'Brokers'), path: '/brokers', icon: Building2 },
  { name: t('nav.charts', 'Charts'), path: '/charts', icon: LineChart },
  { name: t('nav.awards', 'Awards'), path: '/awards', icon: Award },
  { name: t('nav.calendar', 'Calendar'), path: '/calendar', icon: Calendar },
  { name: t('nav.news', 'News'), path: '/news', icon: Newspaper },
  { name: t('nav.analysis_signals', 'Analysis & Signals'), path: '/analysis', icon: Activity },
  { name: t('nav.education', 'Education'), path: '/education', icon: BookOpen },
  { name: t('nav.widgets', 'Widgets'), path: '/widgets', icon: Layers },
  { name: t('nav.traders', 'Traders'), path: '/traders', icon: Users },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [profilePublic, setProfilePublic] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, cycleTheme, isDark, isDarkish } = useTheme();
  const { t, i18n } = useTranslation();
  
  const navItems = getNavItems(t);

  // Get localized path helper
  const getLocalizedPath = (path) => {
    if (i18n.language === defaultLanguage) {
      return path;
    }
    return `/${i18n.language}${path === '/' ? '' : path}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to load user from localStorage
  const loadUserFromStorage = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('fx_token');
    const userData = localStorage.getItem('user') || localStorage.getItem('fx_user');
    if (token && userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed);
        // Default to public if field missing (legacy users)
        setProfilePublic(parsed.public_profile !== false);
      } catch (e) {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  };

  // Toggle public / hidden profile visibility
  const toggleProfileVisibility = async () => {
    const next = !profilePublic;
    setProfilePublic(next); // optimistic
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fx_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ public_profile: next }),
      });
      if (!res.ok) throw new Error('Failed');
      // Persist to local user record too
      const userData = JSON.parse(localStorage.getItem('fx_user') || localStorage.getItem('user') || '{}');
      userData.public_profile = next;
      localStorage.setItem('fx_user', JSON.stringify(userData));
      localStorage.setItem('user', JSON.stringify(userData));
      toast.success(next ? 'Profile is now public' : 'Profile is now hidden');
    } catch (e) {
      setProfilePublic(!next); // revert
      toast.error('Could not update profile visibility');
    }
  };

  useEffect(() => {
    loadUserFromStorage();
  }, [location]);

  // Listen for storage changes (for when profile is updated in dashboard)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'fx_user' || e.key === 'user') {
        loadUserFromStorage();
      }
    };
    
    // Also listen for custom event from same window
    const handleUserUpdate = () => {
      loadUserFromStorage();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userUpdated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('fx_token');
    localStorage.removeItem('fx_user');
    setUser(null);
    navigate('/');
  };

  // Check if path is active (handles language prefixes)
  const isActive = (path) => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const isLangPrefix = pathParts[0] && languageCodes.includes(pathParts[0]);
    const currentPath = isLangPrefix ? '/' + pathParts.slice(1).join('/') : location.pathname;
    
    if (path === '/') {
      return currentPath === '/' || currentPath === '';
    }
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  return (
    <header
      data-testid="navbar"
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b',
        isDarkish
          ? 'bg-[#0a0a0f] border-gray-800/50' 
          : 'bg-[#0a0a0f] border-gray-800/30'
      )}
    >
      <div className="container-app">
        <nav className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center shrink-0"
          >
            <img 
              src={isDarkish ? "/logo-color.png" : "/logo-white.png"}
              alt="FX Astra" 
              className="h-8 w-auto object-contain"
              style={{ minWidth: '120px', maxWidth: '160px' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 mx-4">
            <div className="flex items-center gap-0">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={getLocalizedPath(item.path)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium transition-all duration-150 whitespace-nowrap',
                    isActive(item.path)
                      ? 'text-orange-400 border-b-2 border-orange-400'
                      : 'text-gray-200 hover:text-white border-b-2 border-transparent'
                  )}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Theme Toggle Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleTheme}
              data-testid="theme-toggle"
              className={cn(
                "h-8 w-8 rounded-full transition-colors",
                isDarkish 
                  ? "text-gray-400 hover:text-white hover:bg-white/[0.06]" 
                  : "text-white/80 hover:text-white hover:bg-white/10"
              )}
              title={`Theme: ${theme}`}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4 opacity-60" />}
            </Button>

            {/* User Menu / Auth Buttons */}
            {user ? (
              <>
                {/* Notifications */}
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "relative h-9 w-9 rounded-full",
                    isDarkish 
                      ? "text-gray-400 hover:text-white hover:bg-white/10"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                >
                  <Bell className="h-4 w-4" />
                  <span className={cn(
                    "absolute top-1.5 right-1.5 w-2 h-2 rounded-full ring-2",
                    isDarkish ? "bg-red-500 ring-[#0f0f14]" : "bg-yellow-300 ring-orange-500"
                  )} />
                </Button>

                {/* User Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      data-testid="user-menu-trigger"
                      className={cn(
                        "flex items-center gap-2.5 h-10 pl-1.5 pr-3 rounded-full transition-all",
                        isDarkish 
                          ? "bg-white/5 hover:bg-white/10 border border-gray-800 hover:border-gray-700"
                          : "bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/30"
                      )}
                    >
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className={cn("w-7 h-7 rounded-full object-cover ring-2", isDarkish ? "ring-purple-500/50" : "ring-white/50")} />
                      ) : (
                        <div className={cn("w-7 h-7 rounded-full flex items-center justify-center", isDarkish ? "bg-purple-600" : "bg-white")}>
                          <span className={cn("text-xs font-bold", isDarkish ? "text-white" : "text-orange-600")}>{(user.name || user.email || 'U').charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      <span className={cn("hidden sm:inline text-sm font-medium", isDarkish ? "text-white" : "text-white")}>
                        {user.name?.split(' ')[0] || user.email?.split('@')[0] || 'User'}
                      </span>
                      {user.is_premium && (
                        <Crown className={cn("h-3.5 w-3.5", isDarkish ? "text-amber-400" : "text-yellow-300")} />
                      )}
                      <ChevronDown className={cn(
                        "h-3.5 w-3.5",
                        isDarkish ? "text-gray-500" : "text-gray-400"
                      )} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className={cn(
                      "w-48",
                      isDarkish 
                        ? "bg-[#1a1a24] border-gray-800 text-white"
                        : "bg-white border-gray-200 text-gray-900"
                    )}
                  >
                    <div className="px-3 py-2 border-b" style={{ borderColor: isDarkish ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)' }}>
                      <p className={cn("text-xs font-medium", isDarkish ? "text-gray-400" : "text-gray-500")}>{user.email}</p>
                      {user.is_premium && (
                        <span className="text-[9px] text-amber-400 flex items-center gap-1 mt-0.5"><Crown className="h-2.5 w-2.5" />Premium</span>
                      )}
                    </div>
                    <div className="py-1">
                      <DropdownMenuItem asChild className={cn(
                        "cursor-pointer text-xs",
                        isDarkish ? "text-gray-300 hover:text-white hover:bg-white/5" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                      )}>
                        <Link to={getLocalizedPath("/dashboard")}>
                          <LayoutDashboard className={cn("h-3.5 w-3.5 mr-2", isDarkish ? "text-gray-500" : "text-gray-400")} />
                          {t('nav.dashboard', 'Dashboard')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className={cn(
                        "cursor-pointer text-xs",
                        isDarkish ? "text-gray-300 hover:text-white hover:bg-white/5" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                      )}>
                        <Link to={getLocalizedPath(`/traders/${user.user_id}`)}>
                          <User className={cn("h-3.5 w-3.5 mr-2", isDarkish ? "text-gray-500" : "text-gray-400")} />
                          My Profile
                        </Link>
                      </DropdownMenuItem>
                      {!user.is_premium && (
                        <DropdownMenuItem asChild className="text-amber-500 hover:text-amber-600 hover:bg-amber-500/10 cursor-pointer text-xs">
                          <Link to={getLocalizedPath("/dashboard?tab=subscription")}>
                            <Star className="h-3.5 w-3.5 mr-2" />
                            Upgrade to Pro
                          </Link>
                        </DropdownMenuItem>
                      )}
                    </div>
                    <DropdownMenuSeparator className={isDarkish ? "bg-gray-800" : "bg-gray-200"} />
                    <div className="py-1">
                      <DropdownMenuItem 
                        onClick={handleLogout}
                        data-testid="logout-btn"
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10 cursor-pointer text-xs"
                      >
                        <LogOut className="h-3.5 w-3.5 mr-2" />
                        {t('nav.logout', 'Sign Out')}
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="hidden sm:flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  asChild
                  data-testid="login-btn"
                  className={cn(
                    "rounded-full px-3 h-8 text-xs",
                    isDarkish 
                      ? "text-gray-400 hover:text-white hover:bg-white/10"
                      : "text-white/90 hover:text-white hover:bg-white/10"
                  )}
                >
                  <Link to={getLocalizedPath("/auth")}>{t('nav.login', 'Sign In')}</Link>
                </Button>
                <Button
                  asChild
                  data-testid="register-btn"
                  className={cn(
                    "rounded-full px-4 h-8 text-xs shadow-lg",
                    isDarkish 
                      ? "bg-purple-600 hover:bg-purple-700 text-white"
                      : "bg-white hover:bg-gray-50 text-orange-600 font-semibold"
                  )}
                >
                  <Link to={getLocalizedPath("/auth?tab=register")}>{t('auth.sign_up', 'Get Started')}</Link>
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle — Animated hamburger */}
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-trigger"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              className={cn(
                "lg:hidden relative h-11 w-11 rounded-xl flex items-center justify-center group transition-all duration-300 active:scale-95",
                isDarkish
                  ? "bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] hover:border-orange-500/40"
                  : "bg-white/10 hover:bg-white/20 border border-white/20 hover:border-orange-400/60 backdrop-blur-md",
                mobileOpen && "bg-orange-500/15 border-orange-500/50"
              )}
            >
              {/* Subtle glow on hover/open */}
              <span
                className={cn(
                  "pointer-events-none absolute inset-0 rounded-xl transition-opacity duration-300",
                  "bg-gradient-to-br from-orange-500/20 via-transparent to-purple-500/10",
                  mobileOpen ? "opacity-100" : "opacity-0 group-hover:opacity-60"
                )}
              />
              {/* 3-bar morphing hamburger */}
              <span className="relative w-5 h-[14px] flex flex-col justify-between">
                <span
                  className={cn(
                    "block h-[2px] rounded-full transition-all duration-300 ease-in-out origin-center",
                    mobileOpen
                      ? "translate-y-[6px] rotate-45 bg-orange-400 w-full"
                      : cn("w-full", isDarkish ? "bg-gray-300 group-hover:bg-white" : "bg-white")
                  )}
                />
                <span
                  className={cn(
                    "block h-[2px] rounded-full transition-all duration-200 ease-in-out",
                    mobileOpen
                      ? "opacity-0 scale-x-0"
                      : cn("w-3/4 ml-auto", isDarkish ? "bg-gray-400 group-hover:bg-white" : "bg-white/80 group-hover:bg-white")
                  )}
                />
                <span
                  className={cn(
                    "block h-[2px] rounded-full transition-all duration-300 ease-in-out origin-center",
                    mobileOpen
                      ? "-translate-y-[6px] -rotate-45 bg-orange-400 w-full"
                      : cn("w-1/2 ml-auto", isDarkish ? "bg-gray-500 group-hover:bg-white" : "bg-white/70 group-hover:bg-white")
                  )}
                />
              </span>
            </button>
          </div>
        </nav>

        {/* Mobile Menu — Compact Slide-in Drawer */}
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <div
              className="lg:hidden fixed inset-0 z-[99] bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            {/* Drawer */}
            <aside
              className={cn(
                "lg:hidden fixed top-0 right-0 z-[100] h-[100dvh] w-[86vw] max-w-[340px]",
                "flex flex-col shadow-2xl",
                "animate-in slide-in-from-right duration-200",
                isDarkish
                  ? "bg-[#0a0a0f] border-l border-white/5"
                  : "bg-white border-l border-black/5"
              )}
              data-testid="mobile-menu-drawer"
            >
              {/* Drawer Header */}
              <div className={cn(
                "flex items-center gap-2 px-4 h-12 shrink-0 border-b",
                isDarkish ? "border-white/5" : "border-black/5"
              )}>
                <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center">
                  <img src={isDarkish ? "/logo-color.png" : "/logo-color.png"} alt="FX Astra" className="h-6 w-auto" />
                </Link>
                <div className="flex-1" />
                <button
                  onClick={cycleTheme}
                  className={cn(
                    "p-1.5 rounded-md transition-colors",
                    isDarkish ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-500 hover:text-black hover:bg-black/5"
                  )}
                  title="Theme"
                  data-testid="mobile-theme-toggle"
                >
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "p-1.5 rounded-md transition-colors",
                    isDarkish ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-500 hover:text-black hover:bg-black/5"
                  )}
                  data-testid="mobile-menu-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Search */}
              <div className={cn("px-3 py-2.5 shrink-0 border-b", isDarkish ? "border-white/5" : "border-black/5")}>
                <button
                  onClick={() => { setMobileOpen(false); navigate(getLocalizedPath('/brokers')); }}
                  className={cn(
                    "w-full flex items-center gap-2 h-9 px-3 rounded-md text-xs transition-colors",
                    isDarkish
                      ? "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200"
                      : "bg-black/[0.03] text-gray-500 hover:bg-black/5 hover:text-gray-700"
                  )}
                  data-testid="mobile-search-trigger"
                >
                  <Search className="h-3.5 w-3.5" />
                  <span>Search brokers, news, tools…</span>
                </button>
              </div>

              {/* Nav grid (2 columns, compact) */}
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                <div className={cn("px-3 pt-3 pb-1.5 text-[9px] font-bold uppercase tracking-[0.14em]", isDarkish ? "text-gray-600" : "text-gray-400")}>
                  Navigate
                </div>
                <div className="px-2 grid grid-cols-2 gap-1">
                  {navItems.map((item) => {
                    const Icon = item.icon || Home;
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={getLocalizedPath(item.path)}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                        className={cn(
                          "group flex items-center gap-2 h-10 px-2.5 rounded-md transition-all",
                          active
                            ? isDarkish
                              ? "bg-orange-500/10 text-orange-400 border border-orange-500/20"
                              : "bg-orange-500/10 text-orange-600 border border-orange-500/30"
                            : isDarkish
                              ? "text-gray-400 hover:text-white hover:bg-white/5 border border-transparent"
                              : "text-gray-600 hover:text-black hover:bg-black/[0.04] border border-transparent"
                        )}
                      >
                        <Icon className={cn("h-3.5 w-3.5 shrink-0", active && "text-orange-400")} />
                        <span className="text-xs font-medium truncate">{item.name}</span>
                      </Link>
                    );
                  })}
                </div>

                {/* Quick filters */}
                <div className={cn("px-3 pt-4 pb-1.5 text-[9px] font-bold uppercase tracking-[0.14em]", isDarkish ? "text-gray-600" : "text-gray-400")}>
                  Quick Filters
                </div>
                <div className="px-2 space-y-0.5">
                  {[
                    { to: '/brokers?filter=regulated', label: 'Regulated Brokers', icon: Shield, color: 'text-green-400' },
                    { to: '/brokers?filter=low-spread', label: 'Low Spread', icon: TrendingUp, color: 'text-blue-400' },
                    { to: '/awards', label: 'Top Awards', icon: Crown, color: 'text-yellow-400' },
                    { to: '/news', label: 'Breaking News', icon: Zap, color: 'text-orange-400' },
                  ].map(link => (
                    <Link
                      key={link.to}
                      to={getLocalizedPath(link.to)}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex items-center gap-2.5 h-9 px-2.5 rounded-md text-xs transition-colors",
                        isDarkish ? "text-gray-300 hover:bg-white/5" : "text-gray-700 hover:bg-black/[0.04]"
                      )}
                      data-testid={`mobile-quick-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <link.icon className={cn("h-3.5 w-3.5", link.color)} />
                      <span className="flex-1 truncate">{link.label}</span>
                      <ChevronRight className="h-3 w-3 opacity-40" />
                    </Link>
                  ))}
                </div>

                {/* Language */}
                <div className={cn("px-3 pt-4 pb-1.5 text-[9px] font-bold uppercase tracking-[0.14em]", isDarkish ? "text-gray-600" : "text-gray-400")}>
                  Language
                </div>
                <div className="px-2 pb-3">
                  <div className={cn(
                    "relative rounded-md",
                    isDarkish ? "bg-white/5 border border-white/5" : "bg-black/[0.03] border border-black/5"
                  )}>
                    <select
                    value={i18n.language}
                    onChange={(e) => { i18n.changeLanguage(e.target.value); }}
                    className={cn(
                      "w-full h-9 pl-3 pr-8 bg-transparent text-xs font-medium appearance-none cursor-pointer outline-none focus:ring-1 focus:ring-orange-500/40 rounded-md",
                      isDarkish ? "text-gray-200" : "text-gray-800"
                    )}
                    data-testid="mobile-language-select"
                  >
                      {languages.map(lang => (
                        <option key={lang.code} value={lang.code} className={isDarkish ? "bg-[#12121a] text-gray-200" : "bg-white text-gray-800"}>
                          {lang.name} ({lang.code.toUpperCase()})
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={cn("absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none", isDarkish ? "text-gray-500" : "text-gray-500")} />
                  </div>
                </div>
              </div>

              {/* Footer: Account */}
              <div
                className={cn("shrink-0 border-t px-3 pt-3", isDarkish ? "border-white/5 bg-[#0a0a0f]" : "border-black/5 bg-white")}
                style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
              >
                {user ? (
                  <div className="flex items-center gap-2">
                    <Link
                      to={getLocalizedPath('/dashboard')}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex-1 flex items-center gap-2 h-9 px-3 rounded-md text-xs font-medium transition-colors",
                        isDarkish ? "bg-white/5 text-gray-200 hover:bg-white/10" : "bg-black/[0.04] text-gray-800 hover:bg-black/[0.06]"
                      )}
                      data-testid="mobile-dashboard-btn"
                    >
                      <LayoutDashboard className="h-3.5 w-3.5 text-purple-400" />
                      <span className="truncate">{user.name || user.email?.split('@')[0]}</span>
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setMobileOpen(false); }}
                      className={cn(
                        "h-9 px-3 rounded-md text-xs font-medium transition-colors",
                        "text-red-400 hover:bg-red-500/10"
                      )}
                      data-testid="mobile-logout-btn"
                      title={t('nav.logout', 'Sign Out')}
                    >
                      <LogOut className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Link
                      to={getLocalizedPath('/auth')}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex-1 flex items-center justify-center h-9 rounded-md text-xs font-medium transition-colors",
                        isDarkish ? "bg-white/5 text-gray-200 hover:bg-white/10" : "bg-black/[0.04] text-gray-800 hover:bg-black/[0.06]"
                      )}
                      data-testid="mobile-signin-btn"
                    >
                      {t('nav.login', 'Sign In')}
                    </Link>
                    <Link
                      to={getLocalizedPath('/auth?tab=register')}
                      onClick={() => setMobileOpen(false)}
                      className="flex-1 flex items-center justify-center h-9 rounded-md text-xs font-bold bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                      data-testid="mobile-signup-btn"
                    >
                      {t('nav.register', 'Sign Up')}
                    </Link>
                  </div>
                )}
              </div>
            </aside>
          </>
        )}
      </div>
    </header>
  );
}
